import { useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Link as RouterLink, Navigate, useParams, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  isEditableAfterSubmit,
  recordNoun,
  REFERRAL_STATUS_LABELS,
  referralCopy,
  type ReferralDto,
  type ReferralFormDto,
} from '@hv/shared';
import { ApiError } from '../api/client';
import { keys, useMyReferral } from '../api/hooks';
import { useAuth } from '../components/AuthContext';
import { describeError, useNotify } from '../components/Notify';
import { formFromReferral } from './draft';
import { ClarificationPanel, ReferralActivityLog } from './ReferralClarification';
import { ReferralEditForm } from './ReferralEditForm';
import { ReviewStep } from './ReferralSteps';

const page = { p: 2, maxWidth: 960, mx: 'auto', width: '100%' } as const;

const familyName = (r: ReferralDto) =>
  r.payload.familyLastName ? `${r.payload.familyLastName} family` : 'Untitled referral';

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

const homeOf = (r: ReferralDto) =>
  r.countsTowardAllotment
    ? { to: '/my-recommendations', label: 'My Recommendations' }
    : { to: '/my-referrals', label: 'My Referrals' };

export function ReferralSubmissionView({ referral, config }: { referral: ReferralDto; config: ReferralFormDto }) {
  const { me } = useAuth();
  const { notify } = useNotify();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const t = useMemo(() => referralCopy(config.formCopy), [config.formCopy]);
  const form = useMemo(() => formFromReferral(referral), [referral]);
  const [dirty, setDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const cycles = referral.cycle ? [referral.cycle] : [];
  const home = homeOf(referral);
  const editable = isEditableAfterSubmit(referral.status);
  const editing = editable && params.get('edit') === '1';
  const clarifying = referral.status === 'needs_clarification';

  const setEditing = (on: boolean) => {
    setParams(on ? { edit: '1' } : {}, { replace: true });
    setDirty(false);
    setConfirmDiscard(false);
  };
  const toggleEditing = () => {
    if (!editing) setEditing(true);
    else if (dirty) setConfirmDiscard(true);
    else setEditing(false);
  };
  const reload = () => void qc.invalidateQueries({ queryKey: keys.myReferral(referral.id) });

  return (
    <Box sx={page}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} mb={0.5} flexWrap="wrap">
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="h2">
            {recordNoun(referral.countsTowardAllotment)}: {familyName(referral)}
          </Typography>
          <Chip size="small" color="primary" label={REFERRAL_STATUS_LABELS[referral.status]} />
        </Stack>
        {editable && (
          <Button variant={editing ? 'text' : 'outlined'} data-testid="record-edit-button" onClick={toggleEditing}>
            {editing ? 'Cancel editing' : 'Edit'}
          </Button>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {referral.referenceNumber ? `Reference ${referral.referenceNumber} · ` : ''}Submitted{' '}
        {formatDate(referral.submittedAt)}
      </Typography>
      {clarifying && (
        <ClarificationPanel
          referral={referral}
          blocked={editing && dirty}
          onSent={() => editing && setEditing(false)}
          onReload={reload}
        />
      )}
      {!editing && !clarifying && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {editable
            ? 'This referral was submitted to Admissions. Choose Edit to add a child or update your answers.'
            : 'This referral is archived and can no longer be changed.'}
        </Alert>
      )}
      {editing ? (
        <ReferralEditForm
          key={referral.version}
          referral={referral}
          config={config}
          onDirtyChange={setDirty}
          onSaved={() => setEditing(false)}
          onReload={reload}
        />
      ) : (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <ReviewStep
            form={form}
            edit={() => undefined}
            t={(key, vars) => (key === 'step5_hint' ? 'What you submitted:' : t(key, vars))}
            grades={[]}
            cycles={cycles}
            me={me}
            allotmentLimit={0}
            allotment={null}
            order={config.formOrder}
            hidden={config.hiddenQuestions}
            readOnly
            childStages={referral.childStages}
          />
          <Stack direction="row" justifyContent="flex-end" mt={4} pt={2} sx={{ borderTop: 1, borderColor: 'divider' }}>
            <Button variant="contained" component={RouterLink} to={home.to}>
              Back to {home.label}
            </Button>
          </Stack>
        </Paper>
      )}
      {!editing && <ReferralActivityLog referralId={referral.id} />}
      <Dialog open={confirmDiscard} onClose={() => setConfirmDiscard(false)}>
        <DialogTitle>Discard your unsaved changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have edits on this referral that have not been saved yet. Closing the editor will discard them.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDiscard(false)}>Keep editing</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setEditing(false);
              notify('info', 'Your unsaved changes were discarded');
            }}
          >
            Discard changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Fact({ term, children }: { term: string; children: ReactNode }) {
  return (
    <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
      <Typography component="dt" variant="caption" color="text.secondary">
        {term}
      </Typography>
      <Box component="dd" sx={{ m: 0, mt: 0.5, fontWeight: 600 }}>
        {children}
      </Box>
    </Box>
  );
}

export function ReferralConfirmationPage() {
  const { id = '' } = useParams();
  const referral = useMyReferral(id);
  const { notify, notifyError } = useNotify();

  if (referral.isPending) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
        <CircularProgress aria-label="Loading" />
      </Box>
    );
  }
  if (referral.error) {
    const missing = referral.error instanceof ApiError && referral.error.status === 404;
    return (
      <Box sx={page}>
        <Alert
          severity={missing ? 'warning' : 'error'}
          action={
            <Button component={RouterLink} to="/my-referrals">
              My Referrals
            </Button>
          }
        >
          {missing ? 'This referral was not found.' : `Could not load the referral: ${describeError(referral.error)}`}
        </Alert>
      </Box>
    );
  }
  const r = referral.data;
  if (r.status === 'draft') return <Navigate to={`/my-referrals/${r.id}`} replace />;

  const home = homeOf(r);
  const copy = async () => {
    if (!r.referenceNumber) return;
    try {
      await navigator.clipboard.writeText(r.referenceNumber);
      notify('success', 'Reference number copied');
    } catch (err) {
      notifyError(err, 'Copying the reference number failed');
    }
  };

  return (
    <Box sx={{ ...page, maxWidth: 720 }}>
      <Alert severity="success" sx={{ mb: 2 }}>
        <AlertTitle>Referral submitted</AlertTitle>
        Your {recordNoun(r.countsTowardAllotment).toLowerCase()} of the {r.payload.familyLastName} family was received by the Office of Admission.
      </Alert>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          component="dl"
          sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, m: 0 }}
          data-testid="confirmation-facts"
        >
          <Fact term="Reference number">
            <Stack direction="row" alignItems="center" gap={0.5}>
              <span data-testid="reference-number">{r.referenceNumber ?? '—'}</span>
              {r.referenceNumber && (
                <Tooltip title="Copy reference number">
                  <IconButton size="small" aria-label="Copy reference number" onClick={() => void copy()}>
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Fact>
          <Fact term="Date submitted">{formatDate(r.submittedAt)}</Fact>
          <Fact term="Academic year">{r.cycle?.label ?? '—'}</Fact>
          <Fact term="Family">{r.payload.familyLastName || '—'}</Fact>
          <Fact term="Status">{REFERRAL_STATUS_LABELS[r.status]}</Fact>
        </Box>
        <Typography variant="body2" color="text.secondary" mt={2}>
          A confirmation email with the reference number is on its way to you. It contains no details of the referral.
          If Admissions needs more information, you will be emailed and the referral will show as needing
          clarification in My Referrals.
        </Typography>
        <Stack direction="row" gap={1} mt={3} flexWrap="wrap">
          <Button variant="contained" component={RouterLink} to={home.to}>
            {home.label}
          </Button>
          <Button variant="outlined" component={RouterLink} to="/my-referrals/new">
            Start another referral
          </Button>
          <Button component={RouterLink} to={`/my-referrals/${r.id}`}>
            View submission
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
